import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, fmtPrice } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, X, ChevronDown } from 'lucide-react';

export default function ConsultationPackage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get(`/packages/${slug}`)
      .then(r => setPkg(r.data))
      .catch(() => setPkg(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const book = async () => {
    if (!user) { toast.message('Inicia sesión para reservar'); nav(`/login?next=/asesorias/${slug}`); return; }
    setBooking(true);
    try {
      const r = await api.post('/checkout/session', { package_id: pkg.id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch {
      toast.error('No se pudo iniciar el pago');
      setBooking(false);
    }
  };

  if (loading) return <div className="py-32 text-center text-stilo-ink2">Cargando…</div>;
  if (!pkg) return (
    <div className="py-32 text-center">
      <p className="text-stilo-ink2">Paquete no encontrado.</p>
      <Link to="/asesorias" className="underline text-stilo-ink mt-4 inline-block">Ver todas las asesorías</Link>
    </div>
  );

  return (
    <main className="bg-stilo-bg" data-testid="package-page">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-10 lg:pt-14">
        <Link to="/asesorias" className="inline-flex items-center gap-2 text-sm text-stilo-ink2 hover:text-stilo-ink" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Todas las asesorías
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-end">
          <div className="lg:col-span-7 fade-up">
            <p className="overline">{pkg.modality === 'live' ? `${pkg.duration_minutes} min · Zoom` : 'Asíncrono · 48h'}</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[0.98] font-light">{pkg.name}</h1>
            <p className="mt-6 text-stilo-ink2 text-lg leading-relaxed max-w-xl italic">{pkg.tagline}</p>
            <div className="mt-10 flex items-baseline gap-6 flex-wrap">
              <span className="font-serif text-6xl">{fmtPrice(pkg.price, pkg.currency)}</span>
              <div className="flex flex-col">
                <span className="overline text-stilo-ink3">{pkg.duration_label}</span>
                <span className="text-xs text-stilo-ink3 mt-1">IVA incluido · {pkg.price_label}</span>
              </div>
            </div>
            <button onClick={book} disabled={booking} className="btn-stilo mt-10" data-testid="hero-book">
              {booking ? 'Procesando…' : 'Reservar ahora'} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <div className="lg:col-span-5 fade-up-delay-1">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* DESCRIPCIÓN LARGA */}
      <section className="max-w-3xl mx-auto px-6 lg:px-12 mt-24">
        <p className="font-serif text-2xl lg:text-3xl leading-relaxed text-stilo-ink font-light italic">
          {pkg.description_long}
        </p>
      </section>

      {/* QUÉ INCLUYE */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="overline">Qué incluye</p>
            <h2 className="font-serif text-4xl lg:text-5xl mt-4 leading-tight font-light">Todo lo que necesitas para empezar a transformar tu casa.</h2>
          </div>
          <ul className="md:col-span-8 grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {(pkg.includes || []).map((it, i) => (
              <li key={i} className="flex gap-3 border-t border-stilo-ink/10 pt-5">
                <Check className="w-5 h-5 text-stilo-gold mt-0.5 shrink-0" strokeWidth={1.5} />
                <span className="text-stilo-ink leading-relaxed">{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-32">
        <p className="overline">Cómo funciona</p>
        <h2 className="font-serif text-4xl lg:text-5xl mt-4 max-w-2xl leading-tight font-light">El proceso, paso a paso.</h2>

        <div className={`mt-12 grid grid-cols-1 sm:grid-cols-2 ${(pkg.steps || []).length > 4 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-8`}>
          {(pkg.steps || []).map((s, i) => (
            <div key={i} className="border-t border-stilo-ink pt-5">
              <span className="font-mono text-xs text-stilo-gold tracking-widest">{s.n}</span>
              <h3 className="font-serif text-2xl mt-3 font-light">{s.t}</h3>
              <p className="text-stilo-ink2 mt-3 leading-relaxed text-sm">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PARA QUIÉN ES / NO ES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-stilo-beige p-10 lg:p-12">
            <p className="overline text-stilo-olive">Es para ti si…</p>
            <ul className="mt-8 space-y-5">
              {(pkg.for_who || []).map((it, i) => (
                <li key={i} className="flex gap-3">
                  <Check className="w-5 h-5 text-stilo-gold mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="text-stilo-ink leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-stilo-ink text-stilo-bg p-10 lg:p-12">
            <p className="overline text-stilo-stone/70">Quizá no sea para ti si…</p>
            <ul className="mt-8 space-y-5">
              {(pkg.not_for || []).map((it, i) => (
                <li key={i} className="flex gap-3">
                  <X className="w-5 h-5 text-stilo-gold mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="text-stilo-stone/90 leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 lg:px-12 mt-32">
        <p className="overline">Preguntas frecuentes</p>
        <h2 className="font-serif text-4xl lg:text-5xl mt-4 leading-tight font-light">Lo que más me preguntan.</h2>

        <div className="mt-10 border-t border-stilo-ink/15">
          {(pkg.faqs || []).map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={i} className="border-b border-stilo-ink/15">
                <button onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full flex justify-between items-center py-6 text-left hover:text-stilo-gold transition-colors"
                  data-testid={`faq-${i}`}>
                  <span className="font-serif text-xl pr-6">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-96 pb-6' : 'max-h-0'}`}>
                  <p className="text-stilo-ink2 leading-relaxed">{f.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-32 mb-32">
        <div className="bg-stilo-olive text-stilo-bg p-12 lg:p-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <p className="overline text-stilo-stone/70">{pkg.name}</p>
            <h2 className="font-serif text-4xl lg:text-6xl mt-4 leading-[1.0] font-light">
              ¿Empezamos <em className="italic font-light">esta conversación?</em>
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <p className="overline text-stilo-stone/70 mb-2">Desde</p>
            <p className="font-serif text-5xl">{fmtPrice(pkg.price, pkg.currency)}</p>
            <p className="text-xs text-stilo-stone/60 mt-1">IVA incluido · {pkg.price_label}</p>
            <button onClick={book} disabled={booking} className="btn-stilo btn-stilo-gold mt-6" data-testid="footer-book">
              {booking ? 'Procesando…' : 'Reservar'} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
